import { Upload } from '@mui/icons-material'
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Typography } from '@mui/material'
import { Box } from '@mui/system'
import React, { useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useNotification } from '../../store/redux/hooks'
import { useImportAllDataMutation } from '../../store/redux/reducers/importSlice'
import { createErrorMessage } from '../../store/utils'
import SettingTile from '../../views/GeneralSettingsView/SettingTile'
import ConfirmButton from '../Button/ConfirmButton'

interface GroupImportDialogProps {
  disabled?: boolean
  fullWidth?: boolean
  squareButton?: boolean
}

const GroupImportDialog: React.FC<GroupImportDialogProps> = ({ disabled = false, fullWidth, squareButton = false }) => {
  const { t } = useTranslation()
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const { displayNotification } = useNotification()
  const [importAllData, { isLoading }] = useImportAllDataMutation()

  const handleImportButtonClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      setSelectedFile(file)
      setConfirmOpen(true)
      event.target.value = ''
    }
  }

  const importFile = async (file: File) => {
    try {
      const arrayBuffer = await file.arrayBuffer()
      const uint8Array = new Uint8Array(arrayBuffer)
      await importAllData(uint8Array).unwrap()
      displayNotification({
        message: t('page.adminsPage.generalSettings.groupImport.importSuccess'),
        type: 'success',
      })
    } catch (error) {
      const errorDescription = createErrorMessage(error)
      displayNotification({
        message: errorDescription || t('page.adminsPage.generalSettings.groupImport.importFailed'),
        type: 'error',
      })
    }
  }

  const handleConfirmImport = () => {
    if (selectedFile) {
      importFile(selectedFile)
    }
    setConfirmOpen(false)
  }

  return (
    <>
      <SettingTile
        icon={<Upload />}
        title={t('page.adminsPage.generalSettings.groupImport.title')}
        description={t('page.adminsPage.generalSettings.groupImport.description')}
        onSelect={handleImportButtonClick}
        isLoading={isLoading}
      />

      <input
        type="file"
        onChange={handleFileChange}
        accept=".zip"
        style={{ display: 'none' }}
        ref={fileInputRef}
        disabled={disabled}
        aria-label={t('page.adminsPage.generalSettings.groupImport.import')}
      />

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <Box sx={{ p: 2 }}>
          <DialogTitle>{t('page.adminsPage.generalSettings.groupImport.dialog.title')}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              <Trans
                i18nKey="page.adminsPage.generalSettings.groupImport.dialog.description"
                values={{ fileName: selectedFile?.name }}
              />
            </DialogContentText>
            <Typography sx={{ fontStyle: 'italic', mt: 2, color: 'warning.main' }}>
              {t('page.adminsPage.generalSettings.groupImport.dialog.overwriteWarning')}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              variant="text"
              color="primary"
              size="small"
              onClick={() => setConfirmOpen(false)}
              aria-label={t('page.adminsPage.generalSettings.groupImport.dialog.cancel')}
            >
              {t('page.adminsPage.generalSettings.groupImport.dialog.cancel')}
            </Button>
            <ConfirmButton
              text={t('page.adminsPage.generalSettings.groupImport.dialog.confirm')}
              isDisabled={isLoading}
              onClick={handleConfirmImport}
              aria-label={t('page.adminsPage.generalSettings.groupImport.dialog.confirm')}
            />
          </DialogActions>
        </Box>
      </Dialog>
    </>
  )
}

export default GroupImportDialog
